import { useEffect, useState } from 'react';
import { helpSections, findTopic } from '../content.js';
import HelpNav from './HelpNav.jsx';
import HelpContent from './HelpContent.jsx';

/**
 * The in-app help slide-in drawer: backdrop, Escape-to-close, body-scroll lock
 * while open, section tabs (HelpNav) + topic pills/content (HelpContent).
 *
 * `initialTopic` is `{ sectionId, topicId }` — the topic covering the page the
 * user was on when they opened the drawer (see src/help/content.js's
 * `getTopicForPath`). The drawer re-seeds its active section/topic from it each
 * time it opens, so returning to a different page and reopening help jumps back
 * to that page's own topic.
 */
export default function HelpDrawer({ open, onClose, initialTopic }) {
  const [activeSectionId, setActiveSectionId] = useState(initialTopic.sectionId);
  const [activeTopicId, setActiveTopicId] = useState(initialTopic.topicId);

  // Re-seed the active section/topic every time the drawer is opened.
  useEffect(() => {
    if (!open) return;
    setActiveSectionId(initialTopic.sectionId);
    setActiveTopicId(initialTopic.topicId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-seed on open, not on every prop identity change
  }, [open]);

  // Escape-to-close + body-scroll lock while open.
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const { section } = findTopic(activeSectionId, activeTopicId);

  const handleSelectSection = (sectionId) => {
    setActiveSectionId(sectionId);
    const nextSection = helpSections.find((candidate) => candidate.id === sectionId);
    setActiveTopicId(nextSection?.topics?.[0]?.id);
  };

  return (
    <>
      <div
        className="help-drawer__backdrop"
        data-testid="help-drawer-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="help-drawer offcanvas offcanvas-end show"
        role="dialog"
        aria-modal="true"
        aria-labelledby="helpDrawerTitle"
        data-testid="help-drawer"
      >
        <div className="offcanvas-header help-drawer__header">
          <h5 className="offcanvas-title" id="helpDrawerTitle">
            <i className="fa fa-question-circle me-2" aria-hidden="true" />
            Help &amp; Documentation
          </h5>
          <button
            type="button"
            className="btn-close"
            aria-label="Close help"
            data-testid="help-drawer-close"
            onClick={onClose}
          />
        </div>

        <HelpNav
          sections={helpSections}
          activeSectionId={section.id}
          onSelect={handleSelectSection}
        />

        <div className="offcanvas-body help-drawer__offcanvas-body">
          <HelpContent
            section={section}
            activeTopicId={activeTopicId}
            onSelectTopic={setActiveTopicId}
          />
        </div>
      </div>
    </>
  );
}
